"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { capturePosthogEvent } from "@/lib/posthog-browser";
import { useAuth } from "@/providers/auth";

interface FavoriteMutationContext {
	previousFavorites: string[] | undefined;
}

export type FavoriteMutationInput =
	| string
	| {
			poolAddress: string;
			network?: string;
	  };

function normalizeFavoriteInput(input: FavoriteMutationInput): {
	poolAddress: string;
	network?: string;
} {
	if (typeof input === "string") {
		return { poolAddress: input };
	}
	return input;
}

export const useFavoriteMutation = () => {
	const queryClient = useQueryClient();
	const { user } = useAuth();
	const userId = user?.id;

	return useMutation<void, Error, FavoriteMutationInput, FavoriteMutationContext>({
		mutationFn: async (input) => {
			const { poolAddress } = normalizeFavoriteInput(input);
			const response = await fetch("/api/user/favorites", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ poolAddress }),
			});

			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error || "Failed to add favorite");
			}
		},
		onMutate: async (input) => {
			const { poolAddress } = normalizeFavoriteInput(input);
			if (!userId) return { previousFavorites: undefined };
			const normalizedPoolAddress = poolAddress.toLowerCase();

			await queryClient.cancelQueries({ queryKey: ["favorites", userId] });

			const previousFavorites = queryClient.getQueryData<string[]>(["favorites", userId]);

			queryClient.setQueryData<string[]>(["favorites", userId], (old = []) => {
				if (old.includes(normalizedPoolAddress)) return old;
				return [...old, normalizedPoolAddress];
			});

			return { previousFavorites };
		},
		onError: (_err, _input, context) => {
			if (context?.previousFavorites && userId) {
				queryClient.setQueryData<string[]>(["favorites", userId], context.previousFavorites);
			}
		},
		onSuccess: (_data, input) => {
			const { poolAddress, network } = normalizeFavoriteInput(input);
			void capturePosthogEvent("pool_favorited", {
				pool_address: poolAddress,
				network,
				user_id: userId,
			});
			if (userId) {
				queryClient.invalidateQueries({ queryKey: ["favorites", userId] });
			}
		},
	});
};

export const useUnfavoriteMutation = () => {
	const queryClient = useQueryClient();
	const { user } = useAuth();
	const userId = user?.id;

	return useMutation<void, Error, FavoriteMutationInput, FavoriteMutationContext>({
		mutationFn: async (input) => {
			const { poolAddress } = normalizeFavoriteInput(input);
			const response = await fetch(
				`/api/user/favorites?poolAddress=${encodeURIComponent(poolAddress)}`,
				{
					method: "DELETE",
				},
			);

			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error || "Failed to remove favorite");
			}
		},
		onMutate: async (input) => {
			const { poolAddress } = normalizeFavoriteInput(input);
			if (!userId) return { previousFavorites: undefined };
			const normalizedPoolAddress = poolAddress.toLowerCase();

			await queryClient.cancelQueries({ queryKey: ["favorites", userId] });

			const previousFavorites = queryClient.getQueryData<string[]>(["favorites", userId]);

			queryClient.setQueryData<string[]>(["favorites", userId], (old = []) => {
				return old.filter((addr) => addr.toLowerCase() !== normalizedPoolAddress);
			});

			return { previousFavorites };
		},
		onError: (_err, _input, context) => {
			if (context?.previousFavorites && userId) {
				queryClient.setQueryData<string[]>(["favorites", userId], context.previousFavorites);
			}
		},
		onSuccess: (_data, input) => {
			const { poolAddress, network } = normalizeFavoriteInput(input);
			void capturePosthogEvent("pool_unfavorited", {
				pool_address: poolAddress,
				network,
				user_id: userId,
			});
			if (userId) {
				queryClient.invalidateQueries({ queryKey: ["favorites", userId] });
			}
		},
	});
};
